import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, createCommentVNode as _createCommentVNode, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "mb-x2" }
const _hoisted_2 = { class: "h1" }
const _hoisted_3 = { class: "o-scrollbar-y mt-40 mr-8 pr-20 max-h-screen-40" }
const _hoisted_4 = { class: "flex mb-x4 w-full" }
const _hoisted_5 = { class: "mr-auto" }
const _hoisted_6 = { class: "ml-auto text-right" }
const _hoisted_7 = { class: "min-h-75 flex flex-col justify-center mb-x2" }
const _hoisted_8 = {
  key: 0,
  class: "flex justify-center items-center"
}
const _hoisted_9 = { class: "pl-x2" }
const _hoisted_10 = { key: 2 }
const _hoisted_11 = ["disabled"]

import { BaseProductItem, CheckOutDeliverable, CheckOutDeliverableWithAddress, ClickAndCollectCheckOutDeliverable, CustomerDeliveryCheckOutDeliverable, DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery, ModuleSofaConfigurationProductItem, OutletProductItem, PickUpCheckOutDeliverable } from '@/storecontext/shared/api/server-apis';
import { computed, ref } from 'vue';
import SplitDeliveryItem from '@/storecontext/checkout/ui/splitdelivery/SplitDeliveryItem.vue';
import { useCheckout } from '@/storecontext/checkout/useCheckout';
import { useDeliverableLineItems } from '@/storecontext/checkout/useDeliverableLineItems';
import { useForm } from 'vee-validate';
import { forEach } from 'lodash';
import BlockLabelWithNamedArguments from '@/shared/BlockLabelWithNamedArguments.vue';
import { useStepReset } from '@/storecontext/checkout/useStepReset';
import BaseDialog from '@/shared/dialog/BaseDialog.vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import CheckoutSkeletonLine from '@/storecontext/shared/checkout/ui/CheckoutSkeletonLine.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SplitDeliveryDialog',
  props: {
    deliverable: null,
    isOpen: { type: Boolean }
  },
  emits: ['close'],
  setup(__props: any, { emit }) {

const props = __props as {
    deliverable: CheckOutDeliverable | CheckOutDeliverableWithAddress | CustomerDeliveryCheckOutDeliverable | PickUpCheckOutDeliverable | ClickAndCollectCheckOutDeliverable;
    isOpen: boolean;
}





const labels = useBlockLabels();
const { splitDelivery, getDeliveryDateAndFreightPriceForSplitItems } = useCheckout();
const { handleSubmit, isSubmitting } = useForm();
const { deliverableLineItemsSortedByDeliveryDate } = useDeliverableLineItems();
const resetStep = useStepReset();

const deliveryDateAndFreightPriceMessage = ref<DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery | null>(null);

const selectedItems = ref<ModuleSofaConfigurationProductItem[] | BaseProductItem[] | OutletProductItem[]>([]);
const showSplitDeliverySuccessMessage = ref(false);
const showFreightPriceSkeleton = ref(false);

const sortedLineItems = computed<BaseProductItem[]>(() => deliverableLineItemsSortedByDeliveryDate(props.deliverable));

const setDeliveryDateAndFreightPriceMessage = async(value: ModuleSofaConfigurationProductItem[] | BaseProductItem[] | OutletProductItem[]) => {
    if (!value.length) {
        deliveryDateAndFreightPriceMessage.value = null;
        return;
    }
    showFreightPriceSkeleton.value = !showSplitDeliverySuccessMessage.value;
    showSplitDeliverySuccessMessage.value = false;
    const itemIds = value.map(val => val.id);
    try {
        deliveryDateAndFreightPriceMessage.value = await getDeliveryDateAndFreightPriceForSplitItems(props.deliverable.id, itemIds);
    } finally {
        showFreightPriceSkeleton.value = false;
    }
};

const blockLabelAnimationKey = computed<string>(() => {
    let key = '';
    if (deliveryDateAndFreightPriceMessage.value?.text) {
        forEach(deliveryDateAndFreightPriceMessage.value.text.namedParams, p => key += p);
    }

    return key;
});

const resetState = () => {
    selectedItems.value = [];
    deliveryDateAndFreightPriceMessage.value = null;
    showSplitDeliverySuccessMessage.value = false;
};

const submit = handleSubmit(async(): Promise<void> => {
    const ids = selectedItems.value.map(i => i.id);
    await splitDelivery(props.deliverable.id, ids);
    resetState();
    resetStep();
    showSplitDeliverySuccessMessage.value = true;
    // close dialog if no options are left
    if (sortedLineItems.value.length === 1) {
        setTimeout(close, 2000);
    }
});

const close = (): void => {
    resetState();
    emit('close');
};


return (_ctx: any,_cache: any) => {
  const _component_c_icon = _resolveComponent("c-icon")!

  return (_openBlock(), _createBlock(BaseDialog, {
    "is-open": __props.isOpen,
    class: "py-40 pl-50 pr-30 mx-auto bg-white text-center relative z-1 w-screen-80 max-w-850",
    onClose: close
  }, {
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(submit) && _unref(submit)(...args)))
      }, [
        _createElementVNode("div", _hoisted_1, [
          _renderSlot(_ctx.$slots, "heading", {}, () => [
            _createElementVNode("h3", _hoisted_2, _toDisplayString(_ctx.$dictionary.pluralize(_unref(sortedLineItems).length, _unref(labels).splitDeliveryDialogHeading, _unref(labels).splitDeliveryDialogHeadingPlural)), 1)
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("strong", _hoisted_5, _toDisplayString(_unref(labels).splitDeliveryDialogListProductTitle), 1),
              _createElementVNode("strong", _hoisted_6, _toDisplayString(_unref(labels).splitDeliveryDialogListDateTitle), 1)
            ]),
            _createElementVNode("div", null, [
              _createVNode(_TransitionGroup, {
                name: "o-anim-fade-300",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(sortedLineItems), (item) => {
                    return (_openBlock(), _createBlock(SplitDeliveryItem, {
                      key: item.id,
                      modelValue: selectedItems.value,
                      "onUpdate:modelValue": [
                        _cache[0] || (_cache[0] = ($event: any) => ((selectedItems).value = $event)),
                        setDeliveryDateAndFreightPriceMessage
                      ],
                      item: item,
                      "deliverable-id": __props.deliverable.id,
                      "total-items": _unref(sortedLineItems).length
                    }, null, 8, ["modelValue", "item", "deliverable-id", "total-items"]))
                  }), 128))
                ]),
                _: 1
              }),
              _renderSlot(_ctx.$slots, "default", { close: close })
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_Transition, {
            name: "o-anim-fade-300",
            mode: "out-in"
          }, {
            default: _withCtx(() => [
              (showSplitDeliverySuccessMessage.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_8, [
                    _createVNode(_component_c_icon, { name: "icon-checkmark-medium" }),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.$dictionary.pluralize(selectedItems.value.length, _unref(labels).splitDeliveryDialogMoveProductsConfirmation, _unref(labels).splitDeliveryDialogMoveProductsConfirmationPlural)), 1)
                  ]))
                : (deliveryDateAndFreightPriceMessage.value?.text)
                  ? (_openBlock(), _createBlock(BlockLabelWithNamedArguments, {
                      key: _unref(blockLabelAnimationKey),
                      label: deliveryDateAndFreightPriceMessage.value?.text
                    }, null, 8, ["label"]))
                  : (showFreightPriceSkeleton.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createVNode(CheckoutSkeletonLine, { class: "mb-8" }),
                        _createVNode(CheckoutSkeletonLine, { class: "sm:w-3/4 mx-auto" })
                      ]))
                    : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _createElementVNode("button", {
          class: "c-button c-button--primary",
          type: "submit",
          disabled: !selectedItems.value.length || _unref(isSubmitting)
        }, _toDisplayString(_ctx.$dictionary.pluralize(selectedItems.value.length, _unref(labels).splitDeliveryDialogMoveProductsButton, _unref(labels).splitDeliveryDialogMoveProductsButtonPlural)), 9, _hoisted_11)
      ], 32)
    ]),
    _: 3
  }, 8, ["is-open"]))
}
}

})